/**
 * "Signed in as coach@… · Sign out", just above the Powered by line, for a
 * coach who wants to switch accounts or is on a shared device. Shown only
 * while signed in, and not on the payment steps. Signing out clears the
 * coach's picks and starts the flow over where it began: the event's
 * divisions, bulk's sign-in, or the event list.
 */
import { useEffect, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import type { EmbedClient } from '@/api/embed';
import { useFlow } from '@/state/FlowContext';
import { useFlowNavigate } from '@/state/useFlowNavigate';
import './SignedIn.css';

export function SignedIn({ client }: { client: EmbedClient }) {
  const location = useLocation();
  const navigate = useFlowNavigate();
  const { resetForNewRegistration, resetBulk } = useFlow();
  const [email, setEmail] = useState<string | null>(null);
  // Re-read on every step: signing in or creating a team as a guest sets
  // the token, then moves on.
  const authed = client.isAuthed();

  useEffect(() => {
    if (!authed) { setEmail(null); return; }
    client.getMe().then((me) => setEmail(me.email)).catch(() => setEmail(null));
  }, [client, authed, location.pathname]);

  if (!authed || /\/payment$/.test(location.pathname)) return null;

  const signOut = () => {
    client.signOut();
    resetForNewRegistration();
    resetBulk();
    const ev = matchPath('/events/:eventId/*', location.pathname);
    navigate(
      location.pathname.startsWith('/bulk')
        ? '/bulk/auth'
        : ev?.params.eventId ? `/events/${ev.params.eventId}/register` : '/',
      { replace: true },
    );
  };

  return (
    <div className="tn-signedin">
      {email ? <span className="tn-signedin__who">Signed in as {email}</span> : <span>Signed in</span>}
      <span aria-hidden="true">·</span>
      <button type="button" className="tn-signedin__out" onClick={signOut}>Sign out</button>
    </div>
  );
}
